import { Map, Marker } from "#src/components/Map";
import {
  Button,
  colours,
  DataDisplayWithAdornment,
  EmptyState,
  Icon,
  LoadingState,
  Popover,
  Skeleton,
  type DataDisplayProps,
} from "@validereinc/common-components";
import {
  copyTextToClipboard,
  formatCoordinateDegrees,
  GeocoordinateSimpleValueSchema,
  type GeocoordinateSimpleValueType,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useState } from "react";
import { GeolocateControl } from "react-map-gl";
import { z } from "zod";
import styles from "./GeocoordinateDataDisplay.module.css";
import { useReverseGeolocation } from "./useReverseGeolocation";

const cx = classNames.bind(styles);

export type GeocoordinateDataDisplayProps = Omit<DataDisplayProps, "value"> & {
  coordinates?: GeocoordinateSimpleValueType;
};

const GeocoordinateEmptySchema = z.tuple([
  z.null().optional(),
  z.null().optional(),
]);

export const GeocoordinateDataDisplay = ({
  coordinates,
  alignment,
  isInline,
  isBusy,
  isLoading,
  emptyText = "-",
  showInvalidIndicator = true,
  invalidText = "Unsupported data type",
  className,
  style,
}: GeocoordinateDataDisplayProps) => {
  const [ready, setIsReady] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const [locationDescription, isLocationDescriptionLoading] =
    useReverseGeolocation({
      coordinates: coordinates,
      isEnabled: ready,
    });
  const displayCoordinateDegrees = coordinates
    ? formatCoordinateDegrees(coordinates, 2)
    : emptyText;
  const displayFullCoordinateDegrees = coordinates
    ? formatCoordinateDegrees(coordinates)
    : emptyText;

  const isEmpty =
    !coordinates || GeocoordinateEmptySchema.safeParse(coordinates).success;
  const isInvalid =
    !!GeocoordinateSimpleValueSchema.safeParse(coordinates).error;

  return (
    <DataDisplayWithAdornment
      showAdornment={showInvalidIndicator && !isEmpty && isInvalid}
      alignment={alignment}
      isInline={isInline}
      isBusy={isBusy}
      isLoading={isLoading}
      adornmentSlot={() =>
        !isEmpty && isInvalid ? (
          <Icon
            variant="warning"
            colour={colours.colours.status.warning}
            size={12}
            alt={invalidText}
            aria-roledescription="Warning Icon"
          />
        ) : null
      }
      className={cx("container", className)}
      style={style}
    >
      {!isEmpty ? (
        <Popover
          contentRender={() => (
            <div className={cx("map-container")}>
              <div className={cx("map-description-container")}>
                <Skeleton isLoading={isLocationDescriptionLoading}>
                  <p className={cx("map-description")}>{locationDescription}</p>
                </Skeleton>
              </div>
              <div className={cx("map-render-container")}>
                {!isInvalid ? (
                  isLocationDescriptionLoading ? (
                    <LoadingState
                      label="Map initializing..."
                      size="small"
                    />
                  ) : (
                    <Map
                      initialViewState={{
                        latitude: coordinates[0],
                        longitude: coordinates[1],
                      }}
                      style={{ height: "100%" }}
                    >
                      <>
                        {/* @ts-expect-error TODO: fix marker typing */}
                        <Marker
                          latitude={coordinates[0]!}
                          longitude={coordinates[1]!}
                          colorVariant="navy"
                          label={displayCoordinateDegrees}
                        />
                        <GeolocateControl showAccuracyCircle />
                      </>
                    </Map>
                  )
                ) : (
                  <EmptyState
                    icon={
                      <Icon
                        variant="warning-circle"
                        colour={colours.colours.status.error}
                        size={12}
                        alt={invalidText}
                        aria-roledescription="Error Icon"
                      />
                    }
                    title="Invalid Coordinates"
                    suggestion="They cannot be displayed on a map."
                  />
                )}
              </div>
              <div className={cx("map-coordinate-details")}>
                <p className={cx("map-coordinates")}>
                  {displayFullCoordinateDegrees}
                </p>
                <Button
                  icon={hasCopied ? "check-circle" : "copy-simple"}
                  disabled={hasCopied}
                  variant="outline"
                  size="xx-small"
                  title="Copy to Clipboard"
                  onClick={() =>
                    copyTextToClipboard(displayFullCoordinateDegrees)
                      .then(() => setHasCopied(true))
                      .then(
                        () =>
                          new Promise((res: any) => {
                            setTimeout(() => res(), 2500);
                          })
                      )
                      .then(() => setHasCopied(false))
                  }
                >
                  {hasCopied ? "Copied" : ""}
                </Button>
              </div>
            </div>
          )}
          rootProps={{
            onOpenChange: (newState) => {
              if (newState) setIsReady(true);
            },
          }}
          contentClassName={cx("content-container")}
          shouldDisplayArrow={false}
        >
          <Button
            slotLeft={
              <Icon
                variant="map-trifold"
                size={12}
                className={cx("preview-trigger__icon")}
              />
            }
            variant="text"
            size="xx-small"
            className={cx("preview-trigger")}
            onClick={() => setIsReady(true)}
          >
            {displayCoordinateDegrees}
          </Button>
        </Popover>
      ) : (
        emptyText
      )}
    </DataDisplayWithAdornment>
  );
};
